import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import SimpleRuleEditor from './SimpleRuleEditor';
import FormulaRuleEditor from './FormulaRuleEditor';
import { buildExpression, emptyConditionRow } from './expressionBuilder';
import { astToRows, nonRepresentableReason } from './astToRows';
import { channelOptions } from './channelOptions';

/**
 * Creating and editing a rule, in two modes over ONE expression.
 *
 * Simple mode is a view of the expression, not a second stored format: its rows
 * serialise to the same string the formula box holds, so a rule edited either way
 * is the same rule. That is the whole reason the modes can coexist without one
 * silently discarding the other's work.
 */

RuleEditorDialog.propTypes = {
  open: PropTypes.bool,
  rule: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const newConfig = () => ({
  mode: 'conditions',
  joiner: 'and',
  rows: [emptyConditionRow()],
  sustain: null,
  settle: null,
});

function RuleEditorDialog({ open, rule, onClose, onSave }) {
  const channels = useMemo(() => channelOptions(), []);
  const isEditing = Boolean(rule?.id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState('simple');
  const [config, setConfig] = useState(newConfig());
  const [expression, setExpression] = useState('');
  const [validation, setValidation] = useState(null);
  const [blockedReason, setBlockedReason] = useState(null);
  const [saving, setSaving] = useState(false);
  // Kept apart from `validation`: the formula editor re-validates on every
  // keystroke and would otherwise wipe a server rejection the instant it landed.
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(rule?.name ?? '');
    setDescription(rule?.description ?? '');
    setValidation(null);
    setSaveError(null);
    setSaving(false);

    const rows = rule?.ast ? astToRows(rule.ast) : null;
    if (rule?.expression && !rows) {
      // Opening in simple mode would draw a subset of this rule and silently drop
      // the rest the moment the user saved.
      setMode('formula');
      setExpression(rule.expression);
      setConfig(newConfig());
      setBlockedReason(nonRepresentableReason(rule.ast));
      return;
    }
    setMode('simple');
    setBlockedReason(null);
    setConfig(rows ?? newConfig());
    setExpression(rule?.expression ?? '');
  }, [open, rule]);

  // In simple mode the expression is derived, so it can never disagree with the rows.
  const simpleExpression = useMemo(
    () => (mode === 'simple' ? buildExpression(config) : ''),
    [mode, config]
  );
  const effectiveExpression = mode === 'simple' ? simpleExpression : expression;

  const handleValidation = useCallback((result) => setValidation(result), []);

  const switchMode = (next) => {
    if (!next || next === mode) return;
    if (next === 'formula') {
      // Always allowed: the rows already are an expression.
      setExpression(simpleExpression);
      setMode('formula');
      return;
    }
    if (!validation?.valid) {
      setBlockedReason('Fix the formula before switching back — the simple editor can only show a valid rule.');
      return;
    }
    const rows = astToRows(validation.ast);
    if (!rows) {
      // Told BEFORE the switch, not after the rows have quietly lost half the rule.
      setBlockedReason(nonRepresentableReason(validation.ast));
      return;
    }
    setConfig(rows);
    setBlockedReason(null);
    setMode('simple');
  };

  const canSave = Boolean(name.trim()) && Boolean(effectiveExpression)
    && (mode === 'simple' || validation?.valid);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        ...(rule ?? {}),
        name: name.trim(),
        description,
        expression: effectiveExpression,
      });
      onClose();
    } catch (e) {
      // The server is the authority on whether an expression is valid, so its
      // rejection is shown here rather than assumed impossible.
      const body = e?.response?.data;
      setSaveError(body?.error?.message ?? 'Could not save this rule.');
      if (mode === 'simple') {
        setExpression(effectiveExpression);
        setMode('formula');
      }
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Edit rule' : 'Create rule'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {/* No Tooltip around either button: ToggleButtonGroup injects
                `selected` and `value` by cloning its DIRECT children, and a
                wrapper swallows both, so the wrapped tab never highlights. */}
            <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, v) => switchMode(v)}>
              <ToggleButton value="simple">Simple</ToggleButton>
              <ToggleButton value="formula">Formula</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {mode === 'simple' ? 'Build the rule from dropdowns' : 'Write the rule out yourself'}
            </Typography>
          </Stack>

          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}

          {blockedReason && (
            <Alert severity="info" onClose={() => setBlockedReason(null)}>
              {blockedReason}
            </Alert>
          )}

          {mode === 'simple' ? (
            <>
              <SimpleRuleEditor config={config} onChange={setConfig} channels={channels} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  This rule as a formula:
                </Typography>
                <Box
                  component="code"
                  sx={{
                    display: 'block', fontFamily: 'monospace', fontSize: 13, mt: 0.5,
                    p: 1, bgcolor: 'action.hover', borderRadius: 0.5, wordBreak: 'break-word',
                  }}
                >
                  {simpleExpression || 'Fill in the fields above to build a rule.'}
                </Box>
              </Box>
            </>
          ) : (
            <FormulaRuleEditor
              expression={expression}
              onChange={setExpression}
              onValidation={handleValidation}
              channels={channels}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Saving…' : 'Save rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RuleEditorDialog;
